import { ChangeDetectorRef, Component, computed, effect, inject, NgZone, QueryList, signal, ViewChild, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { GenetricDetailComponent } from '@app/common-codes/components/edit-components/generic-detail.component';
import { TranslationService } from '@app/common-codes/services/TranslationService';
import { GenericValidationService } from '@app/common-codes/services/GenericValidationService';
import { Visit } from '@app/common-codes/models/matwins-tenant/Visit.model';
import { NavigationService } from '@app/common-codes/services/navigation.service';
import { IStaticStylesConfig } from '@app/common-codes/skins/_primitives/IStaticStylesConfig';
import { CurrentTenantService } from '@app/common-codes/services/CurrentTenantService';
import { Visitor } from '@app/common-codes/models/matwins-tenant/Visitor.model';
import { MatwinsSkins } from '@app/common-codes/skins/global.skin';
import { LookupItem, LookupProfile } from '@app/common-codes/services/LookupService';
import { VisitorType } from '../ipad-visitor-type/ipad-visitor-type.component';
import { visitTypeIdOf } from '../../shared/visitor-types';
import { PointOfService } from '@app/common-codes/models/matwins-tenant/PointOfService.model';
import { WizardStepValidator } from '../wizard-step-validator';
import { CheckinShellComponent } from './checkin-shell/checkin-shell.component';
import { MultiSelectComponent } from '@app/common-codes/components/nested-components/multi-select/multi-select.component';
import { PhotoCaptureComponent } from '../photo/photo-capture.component';
import { QrScannerComponent } from '../qr-scanner/qr-scanner.component';
import { AlertModalService, ToastType } from '@app/common-codes/services/AlertModalService';
import { FaceRecognitionService } from '../../../services/face-recognition.service';
import { Face } from '@app/common-codes/models/matwins-tenant/Face.model';
import type { TranslatableItem } from '@app/common-codes/components/nested-components/translatable-entity-modal/translatable-entity.interface';
import type { PolicyViewerAcceptEvent } from './policy/policy-viewer.component';
import type { QuizViewerResultEvent } from './quiz/quiz-viewer.component';
import type { HealthyViewerAcceptEvent } from './healthy/healthy-viewer.component';
import type { EngagementViewerAcceptEvent } from './contractor-checkin/engagement-viewer.component';

/**
 * Classe de base abstraite pour les composants de checkin.
 * Contient toute la logique partagée : navigation, save, print, badge, lookups.
 * Les composants enfants (Visitor, Contractor, Employee) héritent et fournissent
 * leur propre visitorType et template.
 */
@Component({
    template: '',
})
export abstract class CheckinBaseComponent extends GenetricDetailComponent<Visit> {

    override Skin: IStaticStylesConfig = MatwinsSkins.Ipad1;
    override ComponentEntityClass = Visit;
    override isModal = true;

    currentStep = signal(1);
    abstract visitorType: VisitorType | null;

    private readonly cdr = inject(ChangeDetectorRef);

    @ViewChild(CheckinShellComponent) checkinShell?: CheckinShellComponent;
    @ViewChildren(MultiSelectComponent) private multiSelects?: QueryList<MultiSelectComponent>;
    @ViewChild(PhotoCaptureComponent) private photoCapture?: PhotoCaptureComponent;
    @ViewChild(QrScannerComponent) private qrScanner?: QrScannerComponent;
    badgeImageBase64: string | null = null;
    badgeData: any = null;
    rawEmployeesLookup = signal<LookupItem[]>([]);

    employeesLookup = computed(() => {
        const all = this.rawEmployeesLookup();
        if (this.visitorType?.key !== 'employee') return all;
        return all.filter(item => item.additionalData?.['ismanager'] === true);
    });
    rawVisitorsLookup = signal<LookupItem[]>([]);

    visitorsLookup = computed(() => {
        const all = this.rawVisitorsLookup();
        const typeKey = this.visitorType?.key;
        if (!typeKey || !all?.length) return all;

        const targetType = String(visitTypeIdOf(typeKey));

        return all.filter(item => {
            const typesAggreg = item.additionalData?.['visitortypesaggreg'] as string | undefined;
            if (!typesAggreg) return true;
            return typesAggreg.split(',').some(t => t.trim() === targetType);
        });
    });
    visitorLookupProfileLoaded = false;
    currentPos?: PointOfService;
    urlPolitique?: string;
    pdfRead = signal(false);
    policyAccepted = false;

    protected faceRecognitionService = inject(FaceRecognitionService);
    /** Embedding facial extrait a la prise de photo (envoye au checkin). */
    faceEmbedding: number[] | null = null;
    /** Face matchee (lookup local ou serveur) — reutilisee au checkin. */
    matchedFaceId: number | null = null;
    private faceRecognitionProcessing = false;
    private facePhotoVersion = 0;
    private pendingFacePhoto?: string;
    /** Similarite faceres min (metrique Human : >= 0.5 = meme personne). */
    private static readonly FACE_MATCH_THRESHOLD = 0.5;

    protected lookupProfiles: LookupProfile[] = [
        { lookupName: "Employee" },
    ];

    stepValidator = new WizardStepValidator(null, this.editingEntity, this.currentStep);

    canNext = computed(() => {
        const entity = this.editingEntity();
        const step = this.currentStep();
        if (!entity) return false;
        return this.stepValidator.isStepValid(step);
    });

    isLastStep = computed(() => {
        this.editingEntity();
        this.currentStep();
        return this.stepValidator.isLastVisibleStep();
    });

    constructor(
        route: ActivatedRoute,
        router: Router,
        translationService: TranslationService,
        validationService: GenericValidationService,
        ngZone: NgZone,
        public navService: NavigationService,
        public tenantService: CurrentTenantService,
        public override alertModalService: AlertModalService
    ) {
        super(route, router, translationService, validationService, ngZone);

        // Effect uniquement pour tracker les changements de POS (rebuild validator)
        effect(() => {
            const pos = this.tenantService.currentPointOfService();
            this.currentPos = pos ?? undefined;
            if (this.stepValidator) {
                this.stepValidator.rebuild(this.visitorType, this.currentPos);
            }
        });
    }

    protected async imageUrlToBase64(url: string): Promise<string> {
        const response = await fetch(url);
        const blob = await response.blob();
        return new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }

    override async ngOnInit(): Promise<void> {
        super.ngOnInit();

        this.stepValidator.rebuild(this.visitorType, this.currentPos);

        // Charger les lookups UNE SEULE FOIS
        await this.loadLookups();

        // Initialiser l'entité UNE SEULE FOIS
        this.editingEntity.set(this.getEmptyEntity());
        this.initBadgeData();

        this.canNext = this.stepValidator.createCanNextSignal();

        this.initPolicies();

        // Focus initial sur le premier champ de la 1re étape.
        setTimeout(() => this.focusFirstFieldOfCurrentStep(), 0);
    }

    protected initPolicies(): void {
        const policies = this.tenantService.currentPointOfService()?.policies;
        this.translationService.currentLanguage$.subscribe(lang => {
            lang = lang || 'en';

            let policy = policies?.find(p =>
                p.policyType === 1 &&
                p.isLastVersion &&
                p.file?.rawData_Path?.endsWith(`_${lang}.pdf`)
            );

            if (!policy) {
                policy = policies?.find(p =>
                    p.policyType === 1 &&
                    p.isLastVersion &&
                    p.file?.rawData_Path?.endsWith('_en.pdf')
                );
            }

            if (policy) {
                const baseUrl = window.location.origin;
                this.urlPolitique = `${baseUrl}/Documents/${policy.file?.rawData_Path}`;
            } else {
                this.urlPolitique = undefined;
            }
        });
    }

    protected async loadLookups(): Promise<void> {
        const pos = this.tenantService.currentPointOfService();
        if (pos && !this.visitorLookupProfileLoaded) {
            this.lookupProfiles.push({
                lookupName: "Visitor",
                filterMethodName: "GetFilteredVisitors",
                args: {
                    posId: pos?.id || 0,
                    historyDays: pos?.visitorHistoryDays || 0
                }
            });
            this.visitorLookupProfileLoaded = true;
        }
        await this.lookupService.getLookupsByProfiles(this.dataService, this.lookupProfiles);
        this.rawEmployeesLookup.set(this.getLookupData('Employee'));
        this.rawVisitorsLookup.set(this.getLookupData('Visitor'));
    }

    protected initBadgeData(): void {
        const pos = this.tenantService.currentPointOfService();
        this.badgeData = { visit: this.editingEntity(), pos: pos, logoBase64: '' };
        const imagePath = pos?.imageLogo?.rawData_Path;
        if (imagePath) {
            this.imageUrlToBase64('/Documents/' + imagePath).then(base64 => {
                this.badgeData = { ...this.badgeData, logoBase64: base64 };
            });
        }
    }

    override getEmptyEntity(): Visit {
        const visit = new Visit();
        visit.visitor = new Visitor();
        visit.startDate = new Date();
        visit.status = 1; // In
        visit.numberOfNotificationSent = 0;
        visit.fK_PointOfService = this.tenantService?.currentPointOfServiceId() || 0;
        visit.visitor.currentLanguage = this.translationService?.currentLanguageId;

        return visit;
    }

    override parseToEntityInstance(): void {
        if (this.EntityInstance && this.editingEntity()) {
            Object.assign(this.EntityInstance, this.editingEntity());
        }
    }

    // --- Handlers communs ---

    onPdfRead(event: boolean) {
        this.pdfRead.set(event);
    }

    print(badgeBase64: string, successCallBack: (es: any) => void, errorCallBack: (e: any) => void) {
        const windowAny = window as any;
        let obj = {
            function: 'print',
            args: { url: this.tenantService.currentPointOfService()!.urlAddressPrinter, fileBase64: badgeBase64, printerLabelType: this.tenantService.currentPointOfService()!.printerLabelType, printerLabelSize: this.tenantService.currentPointOfService()!.printerLabelSize }
        }
        if (windowAny.webkit && windowAny.webkit.messageHandlers && windowAny.webkit.messageHandlers.MatWins) {
            windowAny.webkit.messageHandlers.MatWins.postMessage(obj).then((message: any) => {
                if (message.isPrinted)
                    successCallBack(message.isPrinted);
                else
                    errorCallBack('printing error : ' + message.error);
            });
        }
    }

    async printViaNetwork(imageBase64: string, printerIp: string): Promise<void> {
        const blob = await fetch(imageBase64).then(r => r.blob());
        const formData = new FormData();
        formData.append('file', blob, 'badge.png');

        await fetch(`http://${printerIp}/ipp/print`, {
            method: 'POST',
            body: blob,
            headers: { 'Content-Type': 'image/png' }
        });
    }

    async performAfterSave() {
        var visit = this.editingEntity();
        if (visit) {
            if (visit.photoFile) {
                await this.uploadFile(visit.photoFile!, visit.id.toString(), 'FK_Image_Photo');
            }
            if (visit.signatureFile) {
                await this.uploadFile(visit.signatureFile!, visit.id.toString(), 'FK_Image_Signature');
            }
        }
    }

    async saveCheckin(): Promise<void> {
        const visit = this.editingEntity();
        if (!visit) return;

        await this.printVisitBadge(visit);

        const savedVisit = await this.postCheckin(visit);
        if (savedVisit) {
            this.goHome();
        }
    }

    /** Imprime le badge de la visite si le POS l'exige. */
    protected async printVisitBadge(visit: Visit): Promise<void> {
        const pos = this.tenantService.currentPointOfService();
        if (pos?.printBadge) {
            const badgeRenderer = this.checkinShell?.badgeRenderer;
            if (badgeRenderer) {
                this.badgeData = { ...this.badgeData, visit: visit };
                const badgeImage = await badgeRenderer.renderBadge();
                if (badgeImage) {
                    this.badgeImageBase64 = badgeImage;
                    this.print(badgeImage, () => console.log('Badge printed'), (err) => {
                        this.alertModalService.showToast('Erreur impression: ' + err, ToastType.Error, 5000, true);
                    });
                }
            }
        }
    }

    /** Construit le FormData attendu par l'endpoint POST 'checkin'. */
    protected buildCheckinFormData(visit: Visit): FormData {
        // Préparer le JSON de la visite (sans les fichiers binaires)
        const visitForJson = { ...visit };
        visitForJson.photoFile = undefined;
        visitForJson.signatureFile = undefined;
        if (visitForJson.fK_Visitor != undefined && visitForJson.fK_Visitor !== 0) {
            visitForJson.visitor = undefined;
        } else if (visitForJson.visitor) {
            visitForJson.visitor.visits = undefined;
        }
        if (visitForJson.employee) {
            (visitForJson as any).employee.visits = undefined;
        }

        // Construire le FormData unique
        const formData = new FormData();
        formData.append('visitJson', JSON.stringify(visitForJson));
        formData.append('langKey', this.translationService.currentLanguage || 'en');

        if (visit.photoFile) {
            formData.append('photo', visit.photoFile);
        }
        if (visit.signatureFile) {
            formData.append('signature', visit.signatureFile);
        }

        // Flags de génération de rapports (overridable par les enfants)
        const reportFlags = this.getReportFlags(visit);
        formData.append('generateCovidReport', String(reportFlags.covid));
        formData.append('generateEngagementReport', String(reportFlags.engagement));
        formData.append('generateQuizReport', String(reportFlags.quiz));

        if (reportFlags.quiz && visit.quizAnswers?.length) {
            formData.append('quizAnswersJson', JSON.stringify(visit.quizAnswers));
        }

        if (this.faceEmbedding?.length) {
            formData.append('faceEmbeddingJson', JSON.stringify(this.faceEmbedding));
        }
        if (this.matchedFaceId) {
            formData.append('faceId', String(this.matchedFaceId));
        }

        return formData;
    }

    /** Envoie la visite à l'endpoint 'checkin'. Retourne la visite sauvegardée ou null. */
    protected async postCheckin(visit: Visit): Promise<Visit | null> {
        // L'acceptation finale date les documents qui n'ont pas de date propre.
        visit.engagementDate = new Date();

        const formData = this.buildCheckinFormData(visit);

        this.loaderService.show();
        try {
            return await this.dataService.execute<Visit>('POST', 'checkin', formData);
        } finally {
            this.loaderService.hide();
        }
    }

    /** Le parcours affiche-t-il un engagement a signer ? */
    protected supportsEngagement = false;

    getCovidItems(): TranslatableItem[] {
        const pos = this.tenantService.currentPointOfService();
        return (pos?.covids || []) as TranslatableItem[];
    }

    getEngagementItems(): TranslatableItem[] {
        const pos = this.tenantService.currentPointOfService();
        return (pos?.engagements || []) as TranslatableItem[];
    }

    getQuizItems(): TranslatableItem[] {
        const pos = this.tenantService.currentPointOfService();
        return (pos?.quizzes || []).map(quiz => ({
            ...quiz,
            children: (quiz.quizResponses || []).map(qr => ({
                id: qr.id,
                description: qr.description || '',
                numero: qr.numero,
                translations: qr.translations || [],
                isRightResponse: qr.isRightResponse
            }))
        })) as TranslatableItem[];
    }

    onQuizCompleted(event: QuizViewerResultEvent): void {
        const visit = this.editingEntity();
        if (visit) {
            visit.quizStatus = event.allAnswered ? (event.passed ? 1 : 0) : undefined;
            visit.quizDate = event.allAnswered ? new Date() : undefined;
            visit.quizAnswers = event.allAnswered ? event.answers : undefined;
            this.editingEntity.set(Object.assign(new Visit(), visit));
        }
    }

    onPolicyViewerAccepted(event: PolicyViewerAcceptEvent): void {
        this.policyAccepted = event.accepted;
        if (!event.accepted) {
            return;
        }

        const visit = this.editingEntity();
        const policies = this.tenantService.currentPointOfService()?.policies;
        const rdlcPolicy = policies?.find(p => p.isLastVersion && p.policyType === 3);

        visit!.fK_PolicyRdlc = rdlcPolicy?.id;
        visit!.agreementsDate = new Date();

        this.translationService.currentLanguage$.subscribe(lang => {
            lang = lang || 'en';
            let pdfPolicy = policies?.find(p =>
                p.policyType === 1 && p.isLastVersion && p.file?.rawData_Path?.endsWith(`_${lang}.pdf`)
            );
            if (!pdfPolicy) {
                pdfPolicy = policies?.find(p =>
                    p.policyType === 1 && p.isLastVersion && p.file?.rawData_Path?.endsWith('_en.pdf')
                );
            }
            visit!.fK_PolicyPdf = pdfPolicy?.id;
        });
    }

    /** Signature de la sante : dans la visionneuse, ou separement (QR). */
    protected applyHealthySignature(event: HealthyViewerAcceptEvent): void {
        const visit = this.editingEntity();
        if (visit) {
            visit.signatureFile = event.signatureFile;
        }
    }

    async onHealthyAccepted(event: HealthyViewerAcceptEvent): Promise<void> {
        const visit = this.editingEntity();
        if (visit) {
            visit.symptoms = event.hasSymptoms;
            this.applyHealthySignature(event);
            visit.allCovidChecked = event.accepted;
            this.editingEntity.set(Object.assign(new Visit(), visit));
        }
    }

    onHealthyRefused(): void {
        this.goHome();
    }

    /** Signature des engagements : dans la visionneuse, ou separement (QR). */
    protected applyEngagementSignature(event: EngagementViewerAcceptEvent): void {
        const visit = this.editingEntity();
        if (visit) {
            visit.signatureFile = event.signatureFile;
        }
    }

    async onEngagementAccepted(event: EngagementViewerAcceptEvent): Promise<void> {
        const visit = this.editingEntity();
        if (visit) {
            this.applyEngagementSignature(event);
            visit.allEngamgementChecked = event.accepted;
            this.editingEntity.set(Object.assign(new Visit(), visit));
        }
    }

    onEngagementRefused(): void {
        this.goHome();
    }

    protected getReportFlags(visit?: Visit): { covid: boolean; engagement: boolean; quiz: boolean } {
        const v = visit ?? this.editingEntity();
        return {
            covid: !!v?.allCovidChecked,
            engagement: this.supportsEngagement && !!v?.allEngamgementChecked,
            quiz: !!(v?.quizStatus != null && v?.quizAnswers?.length)
        };
    }

    // async saveCheckin(): Promise<void> {
    //     //verifie dans le pos si on doit imprimer le badge
    //     const pos = this.tenantService.currentPointOfService();
    //     if (pos?.printBadge) {
    //         const badgeRenderer = this.checkinShell?.badgeRenderer;
    //         if (badgeRenderer) {
    //             const badgeImage = await badgeRenderer.renderBadge();
    //             if (badgeImage) {
    //                 this.badgeImageBase64 = badgeImage;
    //                 this.print(badgeImage, (success) => {
    //                     console.log('Badge printed successfully');
    //                 }, (error) => {
    //                     console.error('Error printing badge:', error);
    //                     //Affiche l'erreur avec le toaster
    //                     this.alertModalService.showToast(
    //                         'Erreur lors de l\'impression du badge : ' + error,
    //                         ToastType.Error,
    //                         5000,
    //                         true
    //                     );
    //                 });
    //             }
    //         }
    //     }
    //     var tempVisit = { ...this.editingEntity() };
    //     var visitForSaving = this.editingEntity();
    //     if (visitForSaving) {
    //         visitForSaving.photoFile = undefined;
    //         visitForSaving.signatureFile = undefined;
    //         if (visitForSaving.fK_Visitor != undefined && visitForSaving.fK_Visitor !== 0) {
    //             visitForSaving.visitor = undefined;
    //         } else if (visitForSaving.visitor) {
    //             visitForSaving.visitor.visits = undefined; // nettoyer la back-reference
    //         }
    //         if (visitForSaving.employee) {
    //             (visitForSaving as any).employee.visits = undefined;
    //         }
    //     }
    //     const savedItem = await this.save();
    //     if (savedItem) {
    //         this.loaderService.show();
    //         var visit = this.editingEntity();
    //         if (visit) {
    //             visit.photoFile = tempVisit.photoFile;
    //             visit.signatureFile = tempVisit.signatureFile;
    //             visit.allCovidChecked = tempVisit.allCovidChecked;
    //             visit.allEngamgementChecked = tempVisit.allEngamgementChecked;
    //             visit.quizAnswers = tempVisit.quizAnswers;
    //             this.editingEntity.set(visit);
    //         }
    //         await this.performAfterSave();
    //         this.loaderService.hide();

    //         // await this.loadLookups();

    //         this.goHome();
    //     }
    // }

    onEmployeeSelectChange(evt: string[]) {
        const visit = this.editingEntity();
        if (!visit) return;

        const selectedIds = (evt ?? [])
            .map(k => Number(k))
            .filter(n => Number.isFinite(n));

        visit.fK_Employee = selectedIds[0];
        visit.selectedPK_Employee = evt;

        this.editingEntity.set(Object.assign(new Visit(), visit));
    }

    onCreateVisitor(fullname: string) {
        const visit = this.editingEntity();
        if (visit) {
            visit.visitor!.fullname = fullname;
            this.editingEntity.set(Object.assign(new Visit(), visit));
        }
    }

    async onVisitorSelectChange(ids: string[]) {
        const visit = this.editingEntity();
        if (!visit) return;

        const id = ids[0];
        if (id?.startsWith('__new__')) {
            visit.fK_Visitor = 0;
            visit.visitor = new Visitor();
            visit.visitor.currentLanguage = this.translationService?.currentLanguageId;
            visit.visitor.selectedPK_Visitor = ids;
        }
        else if (id?.startsWith('emp_')) {
            const empId = id.replace('emp_', '');
            const employeeLookup = this.rawEmployeesLookup().find(e => e.id === empId);
            if (employeeLookup) {
                visit.fK_Visitor = 0;
                visit.visitor = new Visitor();
                visit.visitor.currentLanguage = this.translationService?.currentLanguageId;
                visit.visitor.fullname = employeeLookup.value;
                visit.visitor.email = employeeLookup.additionalData?.['email'] ?? undefined;
                visit.visitor.phoneNumber = employeeLookup.additionalData?.['phoneNumber'] ?? undefined;
                visit.visitor.selectedPK_Visitor = ids;
            }
        }
        else {
            const numId = Number(id);
            const pos = this.tenantService.currentPointOfService();

            let lastVisit = await this.dataService.execute<Visit>('GET', 'getLastVisit', { pkVisitor: numId, fkPos: pos?.id });
            if (lastVisit) {
                visit.fK_Employee = lastVisit.fK_Employee;
                visit.selectedPK_Employee = [lastVisit?.fK_Employee?.toString() || ''];
                visit.company = lastVisit.company;
                visit.fK_Image_Photo = lastVisit.fK_Image_Photo;

                // Vérifier policiesFrequency
                const frequency = pos?.policiesFrequency ?? 0;
                let shouldReusePolicies = true;
                if (frequency > 0 && lastVisit.agreementsDate) {
                    const daysDiff = (Date.now() - new Date(lastVisit.agreementsDate).getTime()) / (1000 * 60 * 60 * 24);
                    if (daysDiff > frequency) {
                        // Forcer re-signature : purger previousVisit
                        lastVisit.fK_Image_Signature = undefined;
                        lastVisit.fK_PolicyPdf = undefined;
                        lastVisit.fK_PolicyRdlc = undefined;
                        lastVisit.agreementsDate = undefined;
                        shouldReusePolicies = false;
                    }
                }

                if (shouldReusePolicies) {
                    visit.fK_Image_Signature = lastVisit.fK_Image_Signature;
                    visit.agreementsDate = lastVisit.agreementsDate;
                    visit.fK_PolicyRdlc = lastVisit.fK_PolicyRdlc;
                    visit.fK_PolicyPdf = lastVisit.fK_PolicyPdf;
                }
            }
            visit.previousVisit = lastVisit ?? undefined;
            visit.fK_Visitor = Number.isFinite(numId) ? numId : 0;
            const visitorLookup = this.visitorsLookup().find(v => v.id === id);
            if (visitorLookup) {
                visit.visitor!.fullname = visitorLookup.value;
                visit.visitor!.email = visitorLookup.additionalData?.['email'] ?? undefined;
                visit.visitor!.phoneNumber = visitorLookup.additionalData?.['phonenumber'] ?? undefined;
            }
            visit.visitor!.selectedPK_Visitor = ids;
        }
        this.editingEntity.set(Object.assign(new Visit(), visit));
    }

    onPhotoCaptured(base64: string): void {
        if (!this.currentPos?.dS_FaceRecognition) return;
        // Une nouvelle photo (retake) remplace la precedente : la derniere
        // capture doit toujours etre celle analysee/stockee.
        this.facePhotoVersion++;
        this.pendingFacePhoto = base64;
        this.matchedFaceId = null;
        void this.runFaceRecognition();
    }

    /**
     * Extrait l'embedding facial de la derniere photo capturee et tente de
     * retrouver un visiteur connu (derniere visite liee au visage le plus
     * proche). Le match ne s'applique que si aucun visiteur n'est deja identifie.
     */
    protected async runFaceRecognition(): Promise<void> {
        if (this.faceRecognitionProcessing) return;
        this.faceRecognitionProcessing = true;

        try {
            while (this.pendingFacePhoto) {
                const photo = this.pendingFacePhoto;
                const version = this.facePhotoVersion;
                this.pendingFacePhoto = undefined;

                const embedding = await this.faceRecognitionService.extractEmbedding(photo);
                if (!embedding || version !== this.facePhotoVersion) continue;

                this.faceEmbedding = embedding;

                // 1. Match local sur le lookup Visitor (faceembedding de la derniere visite)
                let visitorId = this.matchEmbeddingInLookup(embedding);

                // 2. Fallback serveur : faces du POS dont le visiteur n'est pas dans le lookup
                if (!visitorId) {
                    const posId = this.tenantService.currentPointOfServiceId() ?? 0;
                    const faceDataService = this.di.getOrCreateDataService(Face);
                    const match = await faceDataService.execute<{
                        matched: boolean;
                        faceId?: number;
                        visitorId?: number;
                    }>('POST', 'match', { posId, embedding });
                    console.log('[face-reco] reponse api/faces/match:', JSON.stringify(match));
                    if (match?.matched) {
                        visitorId = match.visitorId;
                        this.matchedFaceId = match.faceId ?? null;
                    }
                }

                if (version !== this.facePhotoVersion) continue;

                const visit = this.editingEntity();
                const alreadyIdentified = !!(visit?.fK_Visitor) || !!(visit?.visitor?.fullname);
                if (visitorId && !alreadyIdentified) {
                    await this.onVisitorSelectChange([String(visitorId)]);
                    this.alertModalService.showToast(
                        this.translationService.instant('vues.ipad.face.recognized'),
                        ToastType.Success, 3000
                    );
                }
            }
        } catch (err) {
            console.error('Face recognition error:', err);
        } finally {
            this.faceRecognitionProcessing = false;
        }
    }

    /**
     * Compare l'embedding aux 'faceembedding' exposes par le lookup Visitor
     * via la metrique de similarite faceres de Human (>= seuil = match).
     * Retourne l'id du visiteur le plus proche, ou undefined.
     * Renseigne matchedFaceId (lastfaceid) pour reutilisation au checkin.
     */
    protected matchEmbeddingInLookup(embedding: number[]): number | undefined {
        let bestVisitorId: number | undefined;
        let bestFaceId: number | null = null;
        let bestSimilarity = 0;

        for (const item of this.rawVisitorsLookup()) {
            const json = item.additionalData?.['faceembeddingjson'] as string | undefined;
            if (!json) continue;

            let stored: number[];
            try {
                stored = JSON.parse(json);
            } catch {
                continue;
            }
            if (!Array.isArray(stored) || stored.length !== embedding.length) {
                console.log(`[face-reco] lookup visiteur ${item.id} (${item.value}) ignore — dims ${Array.isArray(stored) ? stored.length : 'non-tableau'} vs ${embedding.length}`);
                continue;
            }

            const similarity = this.faceRecognitionService.similarity(embedding, stored);
            console.log(`[face-reco] lookup visiteur ${item.id} (${item.value}) — faceId ${item.additionalData?.['lastfaceid']} — similarity: ${similarity.toFixed(4)}`);

            if (similarity > bestSimilarity) {
                bestSimilarity = similarity;
                bestVisitorId = Number(item.id);
                bestFaceId = Number(item.additionalData?.['lastfaceid']) || null;
            }
        }

        console.log(`[face-reco] lookup match — best similarity: ${bestSimilarity.toFixed(4)} (seuil ${CheckinBaseComponent.FACE_MATCH_THRESHOLD})`);

        if (bestSimilarity < CheckinBaseComponent.FACE_MATCH_THRESHOLD) return undefined;

        this.matchedFaceId = bestFaceId;
        return bestVisitorId;
    }

    onPhotoFile(file: File): void {
        var visit = this.editingEntity();
        if (visit) {
            visit.photoFile = file;
        }
    }

    onPolicyAcceptedChange(event: any): void {
        if (event.target.checked) {
            const visit = this.editingEntity();
            const rdlcPolicy = this.tenantService.currentPointOfService()?.policies?.find(p => p.isLastVersion && p.policyType === 3);
            visit!.fK_PolicyRdlc = rdlcPolicy?.id;
            visit!.agreementsDate = new Date();

            const policies = this.tenantService.currentPointOfService()?.policies;
            this.translationService.currentLanguage$.subscribe(lang => {
                lang = lang || 'en';

                let pdfPolicy = policies?.find(p =>
                    p.policyType === 1 &&
                    p.isLastVersion &&
                    p.file?.rawData_Path?.endsWith(`_${lang}.pdf`)
                );

                if (!pdfPolicy) {
                    pdfPolicy = policies?.find(p =>
                        p.policyType === 1 &&
                        p.isLastVersion &&
                        p.file?.rawData_Path?.endsWith('_en.pdf')
                    );
                }

                if (pdfPolicy) {
                    visit!.fK_PolicyPdf = pdfPolicy?.id;
                } else {
                    visit!.fK_PolicyPdf = undefined;
                }
            });
        } else {
            this.policyAccepted = false;
        }
    }

    onBadgeImageReady(base64: string): void {
        this.badgeImageBase64 = base64;
    }

    onSignatureChange(file: File | null): void {
        var visit = this.editingEntity();
        if (visit) {
            visit.signatureFile = file ?? undefined;
            this.editingEntity.set(Object.assign(new Visit(), visit));
        }
    }

    // --- Navigation ---

    /**
     * Met le focus sur le premier champ de l'étape affichée.
     * Appelé de façon SYNCHRONE depuis les handlers de navigation : sur iPad,
     * le clavier ne s'ouvre que si focus() se produit dans le contexte du geste
     * utilisateur — un setTimeout le ferait sortir du geste et le focus serait
     * ignoré. Si l'étape commence par une photo ou un scan QR, on ne focus
     * rien (pas de clavier).
     */
    protected focusFirstFieldOfCurrentStep(): void {
        this.cdr.detectChanges(); // rend la nouvelle étape et met à jour les ViewChild dans le même geste

        // Étape avec photo ou scan QR → pas de focus (pas de clavier).
        if (this.photoCapture || this.qrScanner) return;

        const firstSelect = this.multiSelects?.first;
        if (firstSelect && !firstSelect.selectedKeys?.length) {
            firstSelect.openDropdown();
        }
    }

    goBack() {
        const prev = this.stepValidator.goBack();
        if (prev === null) {
            this.navService.goBack();
            return;
        }
        this.focusFirstFieldOfCurrentStep();
    }

    goForward() {
        const next = this.stepValidator.goForward();
        if (next === null) {
            this.saveCheckin();
            return;
        }
        this.focusFirstFieldOfCurrentStep();
    }

    goHome() {
        const route = `/ipad/home`;
        this.navService.navigateTo(route);
    }
}
